import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SettingsHeader } from '../Settings';
import CommonSwitch from '../../Common/CommonSwitch';
import './../Setting.scss';
import PropTypes from 'prop-types';
import SDK from '../../../SDK';
import { PrivacySettingsAction } from '../../../../Actions/PrivacySettingsAction';
import { getConnectionStatus, dispatchErrorMessage } from '../../Common/FileUploadValidation';
import { NextArrowPrivacy } from '../../../../assets/images';
import Blocked from '../BlockedContacts';
import { PRIVACY_STATUS_MAPPER, privacyWithMode } from '../../../../Helpers/Constants';
import { PROFILE_HIDE, READ_RECEIPTS } from '../../../../Helpers/Chat/Constant';
import logger from '../../../../Helpers/logger';
import DisappearingMessages from './DisappearingMessages';

const PrivacyToggle = React.memo(({ id, label, defaultValue, onChange, reversed = false }) => {
    const [isOn, setIsOn] = useState(defaultValue === 1);

    useEffect(() => {
        setIsOn(defaultValue === 1);
    }, [defaultValue]);

    const handleToggle = () => {
        // If network is not connected, prevent toggling and show error
        const conn = getConnectionStatus();
        if (conn !== 'CONNECTED') {
            // show a standard offline toast
            dispatchErrorMessage();
            return;
        }

        logger.debug('Toggling privacy setting:', id); // Debug log
        const newValue = !isOn;
        setIsOn(newValue);
        onChange(id, newValue ? 1 : 0);
    };

    return (
        <li className="setting-list">
            <div className="setting-list-option">
                <div>
                    <div className="setting_label-text">{label}</div>
                    <div className="privacy_des">
                        {id === 'lastSeen'
                            ? 'Others can see when you were last online'
                            : id === 'seenReceipt'
                              ? READ_RECEIPTS
                              : id === 'hideProfile'
                                ? PROFILE_HIDE
                                : ''}
                    </div>{' '}
                </div>
                <CommonSwitch
                    id={id}
                    checked={reversed ? !isOn : isOn}
                    handleClick={handleToggle}
                    disabled={getConnectionStatus() !== 'CONNECTED'}
                />
            </div>
        </li>
    );
});

const PrivacySettings = ({ handleBackToSetting }) => {
    const dispatch = useDispatch();
    const privacySettingsData = useSelector(state => state.privacySettingsData);
    const lastSeenSetting = parseInt(localStorage.getItem('hk:lastSeen') || '0');
    const hideOnlineSetting = parseInt(localStorage.getItem('hk:hideOnline') || '1');
    const [showBlocked, setShowBlocked] = useState(false);

    const [activeView, setActiveView] = useState('main');

    // Fetch fresh data every time the component mounts (is rendered/shown to the user)
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await SDK.getUserPrivacySettings();
                const lastSeen = localStorage.getItem('hk:lastSeen');
                const hideOnline = localStorage.getItem('hk:hideOnline');
                if (response && response.statusCode === 200) {
                    dispatch(PrivacySettingsAction(response.privacySettings));
                }
                if (response?.privacySettings) {
                    response.privacySettings.lastSeen = response?.privacySettings?.lastSeen ?? parseInt(lastSeen) ?? 0;
                    response.privacySettings.onlineStatus =
                        response?.privacySettings?.onlineStatus ?? parseInt(hideOnline) ?? 1;
                }
            } catch (error) {
                logger.error('Error fetching privacy settings:', error);
            }
        };
        fetchData();
    }, [dispatch]);

    const handleSettingChange = async (key, value) => {
        // Optimistic update
        const currentSettings = privacySettingsData?.privacySettings || {};
        const updatedSettings = { ...currentSettings, [key]: value };

        // We need to keep the same structure as the store expects
        dispatch(PrivacySettingsAction({ ...privacySettingsData, updatedSettings }));

        try {
            const type = PRIVACY_STATUS_MAPPER[key];
            if (!type) {
                logger.error(`Unknown privacy setting key: ${key}`);
                return;
            }
            const action = value.toString();
            const isNewMode = privacyWithMode.includes(type);
            const response = await SDK.updateUserPrivacySettings(type, action, isNewMode);

            if (response && response.statusCode === 200) {
                // Success - strictly speaking we could fetch again or just rely on the optimistic update
                // If the server returns the updated object, we could dispatch that.

                // save features to local storage as api doesn't return it correctly
                if (['lastSeen', 'hideOnline'].includes(key)) {
                    localStorage.setItem(`hk:${key}`, value.toString());
                    // Optimistic update - We need to keep the same structure as the store expects
                    dispatch(PrivacySettingsAction({ ...privacySettingsData, updatedSettings }));
                }
            } else {
                // Revert on failure
                dispatch(PrivacySettingsAction(privacySettingsData));
                logger.error('Failed to update setting.');
            }
        } catch (err) {
            // Revert
            dispatch(PrivacySettingsAction(privacySettingsData));
            logger.error('Error updating setting:', err.message);
        }
    };

    // Safe access to nested properties with fallback to top-level
    // This handles cases where properties might be at the root (like seenReceipt in some cases) or nested
    const hideProfile = privacySettingsData?.privacySettings?.hideProfile ?? privacySettingsData?.hideProfile ?? 0;
    const seenReceipt = privacySettingsData?.privacySettings?.seenReceipt ?? privacySettingsData?.seenReceipt ?? 0;
    const lastSeen =
        lastSeenSetting ?? privacySettingsData?.privacySettings?.lastSeen ?? privacySettingsData?.lastSeen ?? 0;
    const hideOnline =
        privacySettingsData?.privacySettings?.onlineStatus ??
        privacySettingsData?.onlineStatus ??
        hideOnlineSetting ??
        1;

    return (
        <>
            {activeView === 'main' && (
                <div className="setting-container">
                    <div>
                        <div className="settinglist">
                            <SettingsHeader label={'Privacy'} handleBackFromSetting={handleBackToSetting} />
                            <div className="privacy-cards-container">
                                <ul className="setting-list-ul privacy-list-ul privacy-card">
                                    <PrivacyToggle
                                        id="lastSeen"
                                        label="Last Seen"
                                        defaultValue={lastSeen}
                                        onChange={handleSettingChange}
                                    />
                                    <PrivacyToggle
                                        id="hideProfile"
                                        label="Profile Photo"
                                        defaultValue={hideProfile}
                                        onChange={handleSettingChange}
                                    />
                                    <PrivacyToggle
                                        id="seenReceipt"
                                        label="Read Receipts"
                                        defaultValue={seenReceipt}
                                        onChange={handleSettingChange}
                                    />
                                </ul>

                                <ul className="setting-list-ul privacy-list-ul privacy-card">
                                    <div
                                        className="blcoked-list-in-privacy"
                                        onClick={() => setActiveView('disappearing')}
                                    >
                                        <div>
                                            <div className="setting_label-text">Disappearing Messages</div>
                                            <div className="privacy_des">Change App lock settings</div>
                                        </div>
                                        <div className="next_arrow_privacy">
                                            <i className="iconNext">
                                                <NextArrowPrivacy />
                                            </i>
                                        </div>
                                    </div>
                                </ul>

                                <ul className="setting-list-ul privacy-list-ul privacy-card">
                                    <div className="blcoked-list-in-privacy" onClick={() => setActiveView('blocked')}>
                                        <div className="setting_label-text">Blocked Contacts</div>
                                        <div className="next_arrow_privacy">
                                            <i className="iconNext">
                                                <NextArrowPrivacy />
                                            </i>
                                        </div>
                                    </div>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {activeView === 'blocked' && <Blocked handleBackToSetting={() => setActiveView('main')} />}
            {activeView === 'disappearing' && (
                <DisappearingMessages handleBackToSetting={() => setActiveView('main')} />
            )}
        </>
    );
};

PrivacySettings.propTypes = {
    handleBackToSetting: PropTypes.func,
};

export default PrivacySettings;
